import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationTemplateKey } from '../../services/slackService';
import { Card, CardContent } from '../ui/card';
import { NotificationCardHeader } from './NotificationCardHeader';
import { NotificationMessageEditor } from './NotificationMessageEditor';
import { NotificationResetFooter } from './NotificationResetFooter';

export { renderSlackMrkdwn, renderTemplatePreview, findInvalidPlaceholders } from './slackMrkdwn';

interface NotificationTemplateEditorProps {
  templateKey: NotificationTemplateKey;
  body: string;
  enabled: boolean;
  allowedParams: string[];
  error?: string | null;
  isResetting?: boolean;
  onBodyChange: (value: string) => void;
  onEnabledChange: (value: boolean) => void;
  onReset: () => void;
}

/**
 * Editor for a single notification template: an editable body field, the list
 * of supported placeholders (click to insert), a live preview and inline
 * validation feedback. One reusable component rendered per template key.
 */
export const NotificationTemplateEditor: React.FC<NotificationTemplateEditorProps> = ({
  templateKey,
  body,
  enabled,
  allowedParams,
  error,
  isResetting,
  onBodyChange,
  onEnabledChange,
  onReset,
}) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);
  const keyLower = templateKey.toLowerCase();
  const testIdPrefix = `notification-template-${keyLower}`;

  const label = t(`notificationTemplates.keys.${templateKey}.label`, {
    defaultValue: templateKey,
  });
  const description = t(`notificationTemplates.keys.${templateKey}.description`, {
    defaultValue: '',
  });

  return (
    <Card data-testid={testIdPrefix}>
      <NotificationCardHeader
        title={label}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((prev) => !prev)}
        enabled={enabled}
        onEnabledChange={onEnabledChange}
        testIdPrefix={testIdPrefix}
      />

      {expanded && (
        <>
          <CardContent className="space-y-4">
            {description && <p className="text-sm text-muted-foreground">{description}</p>}

            <NotificationMessageEditor
              id={`template-body-${keyLower}`}
              testIdPrefix={testIdPrefix}
              body={body}
              onBodyChange={onBodyChange}
              allowedParams={allowedParams}
              error={error}
              placeholdersDisplay="described"
            />
          </CardContent>
          <NotificationResetFooter
            onReset={onReset}
            isResetting={isResetting}
            testId={`${testIdPrefix}-reset`}
          />
        </>
      )}
    </Card>
  );
};
